import { clamp, range } from 'ramda';
import { padToLength, strToInt } from './stringUtils';
import {
  IDate,
  IEkonomiskAnalysDates,
  IEkonomiskAnalysFilledDates,
  IFilledDate,
} from '../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/AnalysSettingsHandler/AnalysSettingsHandler';
import * as dateFns from 'date-fns';
import { sv } from 'date-fns/locale';
import { stringCompare } from './mergeSort';
import { namedError } from './Logger';
dateFns.setDefaultOptions({ locale: sv });

/** @deprecated */
export interface IDateParts {
  year?: number;
  month?: number;
  day?: number;
}

/** @deprecated */
export interface IFilledDateParts {
  year: number;
  month: number;
  day: number;
}

export enum WeekDay {
  Monday,
  Tuesday,
  Wednesday,
  Thursday,
  Friday,
  Saturday,
  Sunday,
}

export enum DateInputType {
  year = 0,
  yearMonth = 1,
  yearMonthDay = 2,
}

const MIN_YEAR = 100;
const MAX_YEAR = 9999;
const MIN_DATE_TIME = dateFns.parseISO('0100-01-01 00:00:00.000');
const MAX_DATE_TIME = dateFns.parseISO('9999-12-31 23:59:59.999');
const MIN_DATE = dateFns.parseISO('0100-01-01');
const MAX_DATE = dateFns.parseISO('9999-12-31');
const WEEKDAYNAMES = [
  'måndag',
  'tisdag',
  'onsdag',
  'torsdag',
  'fredag',
  'lördag',
  'söndag',
];
const WEEKDAYNAMES_SHORT = ['må', 'ti', 'on', 'to', 'fr', 'lö', 'sö'];
const MONTHNAMES = [
  'januari',
  'februari',
  'mars',
  'april',
  'maj',
  'juni',
  'juli',
  'augusti',
  'september',
  'oktober',
  'november',
  'december',
];
const MONTHNAMES_SHORT = [
  'jan',
  'feb',
  'mar',
  'apr',
  'maj',
  'jun',
  'jul',
  'aug',
  'sep',
  'okt',
  'nov',
  'dec',
];
const QUARTERNAMES = ['q1', 'q2', 'q3', 'q4'];

const standardMonths = range(1, 13);

export const isStandardMonth = (month: number | string) => {
  return standardMonths.includes(Number(month));
};

/** Gets year from a date string in the format: YYYY-MM-DD */
/** @deprecated */
export const getYearFromDate = (date: string): number => {
  const [yearString] = date.split('-');

  const year = Number.parseInt(yearString, 10);

  return year;
};

/** @deprecated */
const getYearOnly = (original: string): IDateParts => {
  const today = new Date();
  const currentYearFirstDigits = today.getFullYear().toString().slice(0, 2);
  const fullYearString = `${currentYearFirstDigits}${original}`;
  const year =
    original.length === 2
      ? parseInt(fullYearString, 10)
      : original.length === 4
      ? parseInt(original, 10)
      : undefined;

  return {
    year,
  };
};

/** @deprecated */
const getYearMonth = (original: string): IDateParts => {
  let year: number | undefined;
  let month: number | undefined;

  if (original.length === 3 || original.length === 4) {
    // YYM eller YYMM...
    const today = new Date();
    const currentYearFirstDigits = today.getFullYear().toString().slice(0, 2);
    const fullYearString = `${currentYearFirstDigits}${original.slice(0, 2)}`;
    year = parseInt(fullYearString, 10);
    month = parseInt(padToLength(original.slice(2), 2, '0'), 10);
  } else if (original.length >= 5) {
    // YYYYM eller YYYYMM...
    year = parseInt(original.slice(0, 4), 10);
    month = parseInt(padToLength(original.slice(4), 2, '0'), 10);
  }

  return { year, month };
};

/** @deprecated */
const getYearMonthDate = (original: string): IDateParts => {
  let year: number | undefined;
  let month: number | undefined;
  let day: number | undefined;

  const today = new Date();
  const currentYearFirstDigits = today.getFullYear().toString().slice(0, 2);
  const fullYearString = `${currentYearFirstDigits}${original.slice(0, 2)}`;
  if (original.length === 4) {
    // YYMD
    year = parseInt(fullYearString, 10);
    month = parseInt(padToLength(original.slice(2, 3), 2, '0'), 10);
    day = parseInt(padToLength(original.slice(3), 2, '0'), 10);
  }
  if (original.length === 5 || original.length === 6) {
    // YYMMD eller YYMMDD
    year = parseInt(fullYearString, 10);
    month = parseInt(original.slice(2, 4), 10);
    day = parseInt(padToLength(original.slice(4), 2, '0'), 10);
  } else if (original.length >= 7) {
    // YYYYMMD eller YYYYMMDD
    year = parseInt(original.slice(0, 4), 10);
    month = parseInt(original.slice(4, 6), 10);
    day = parseInt(padToLength(original.slice(6), 2, '0'), 10);
  }

  return { year, month, day };
};

/** @deprecated */
export const getDateParts = (
  unformated: string,
  dateType?: DateInputType
): IDateParts => {
  const baseText = unformated.replace(/[-./]/g, '');
  let year: number | undefined;
  let month: number | undefined;
  let day: number | undefined;

  switch (dateType) {
    case DateInputType.year:
      year = getYearOnly(baseText).year;
      break;
    case DateInputType.yearMonth: {
      const yearMonth = getYearMonth(baseText);
      year = yearMonth.year;
      month = yearMonth.month;
      break;
    }
    case DateInputType.yearMonthDay: {
      const yearMonthDate = getYearMonthDate(baseText);
      year = yearMonthDate.year;
      month = yearMonthDate.month;
      day = yearMonthDate.day;
      break;
    }
  }
  return { year, month, day };
};

/** @deprecated */
export const datePartsToCompletedDateString = (parts: IDateParts): string => {
  const today = new Date();
  if (parts.day && parts.month) {
    return `${parts.year}-${padToLength(parts.month, 2, '0')}-${padToLength(
      parts.day,
      2,
      '0'
    )}`;
  }
  if (parts.month) {
    return `${parts.year}-${padToLength(parts.month, 2, '0')}-01`;
  }
  if (parts.year) {
    return `${parts.year}-${padToLength(today.getMonth() + 1, 2, '0')}-01`;
  } else {
    return '';
  }
};

/** @deprecated */
export const ekonomiskAnalysDatesToDateString = (
  dates: IEkonomiskAnalysDates | IEkonomiskAnalysFilledDates,
  intervalType?: DateInputType,
  isToDate: boolean = false
): string => {
  const useFromDate = !isToDate || !dates.to.year;
  const year = useFromDate ? dates.from.year : dates.to.year;
  const month = useFromDate ? dates.from.month : dates.to.month;
  const day = useFromDate ? dates.from.day : dates.to.day;
  if (intervalType === undefined) {
    return '';
  }
  const yearString = year;
  const monthString =
    intervalType > DateInputType.year && month
      ? padToLength(month, 2, '0')
      : undefined;
  const dayString =
    intervalType > DateInputType.yearMonth && day
      ? padToLength(day, 2, '0')
      : undefined;
  return [yearString, monthString, dayString].filter(Boolean).join('-');
};

/** @deprecated */
export const dateHasError = (
  dates: IEkonomiskAnalysDates,
  isInterval: boolean
): boolean => {
  return Boolean(dates.from.error) || (Boolean(dates.to.error) && isInterval);
};

/** @deprecated */
export const getCurrentDateString = (restrictingInputType?: DateInputType) => {
  const dateString = new Date().toLocaleDateString('sv-SE');
  return restrictDateString(dateString, restrictingInputType);
};

/** @deprecated */
export const getCurrentTimeString = () => {
  const timeString = new Date().toLocaleTimeString('sv-SE');
  return timeString.slice(0, 5);
};

/** @deprecated */
export const getCurrentDateTimeString = () => {
  const dateTimeString = new Date().toLocaleString('sv-SE');
  return dateTimeString.slice(0, 16);
};

export const getCurrentDateTimeFileName = () => {
  return getCurrentDateTimeString().replace(':', '.');
};

/** @deprecated */
export const getCurrentDate = (restrictingInputType?: DateInputType): IDate => {
  const today = new Date();
  return {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: 1,
    userString: getCurrentDateString(restrictingInputType),
  };
};

/** @deprecated */
export const getCurrentFilledDate = (): IFilledDate => {
  const today = new Date();
  return {
    year: today.getFullYear(),
    month: today.getMonth() + 1,
    day: 1,
  };
};

/** @deprecated */
export const getDefaultDate = (
  dates: IEkonomiskAnalysDates,
  restrictingInputType: DateInputType
): IEkonomiskAnalysDates => {
  return {
    from: {
      ...dates.from,
      month:
        restrictingInputType > DateInputType.year
          ? dates.from.month ?? 1
          : undefined,
      day:
        restrictingInputType > DateInputType.yearMonth
          ? dates.from.day ?? 1
          : undefined,
    },
    to: {
      ...dates.to,
      month:
        restrictingInputType > DateInputType.year
          ? dates.to.month ?? 1
          : undefined,
      day:
        restrictingInputType > DateInputType.yearMonth
          ? dates.to.day ?? 1
          : undefined,
    },
  };
};

export const getDatePlaceholder = (intervalType?: DateInputType): string => {
  switch (intervalType) {
    case DateInputType.year:
      return 'ÅÅÅÅ';
    case DateInputType.yearMonth:
      return 'ÅÅÅÅ-MM';
    case DateInputType.yearMonthDay:
      return 'ÅÅÅÅ-MM-DD';
    default:
      return '';
  }
};

/** @deprecated */
export const getUserString = (
  date: IDate,
  dateInputType?: DateInputType,
  required?: boolean
): string => {
  const userString =
    date.error || (date.userString === '' && !required)
      ? date.userString
      : datePartsToCompletedDateString(date);

  return restrictDateString(userString, dateInputType);
};

/** @deprecated */
export const restrictDateString = (
  str: string,
  dateInputType?: DateInputType
): string => {
  if (dateInputType === undefined) {
    return str; // TODO: might need to change to ''
  }

  switch (dateInputType) {
    case DateInputType.year:
      return str.slice(0, Math.min(4, str.length));
    case DateInputType.yearMonth:
      return str.slice(0, Math.min(7, str.length));
    case DateInputType.yearMonthDay:
      return str.slice(0, Math.min(10, str.length));
  }
};

/** @deprecated */
export const datesWithUpdatedUserStrings = (
  dates: IEkonomiskAnalysDates,
  intervalTyp?: DateInputType
): IEkonomiskAnalysDates => {
  return {
    from: {
      ...dates.from,
      userString: getUserString(dates.from, intervalTyp),
    },
    to: {
      ...dates.to,
      userString: getUserString(dates.to, intervalTyp),
    },
  };
};

/** @deprecated */
const updateDateByDateInputType = (
  date: IDate,
  originalStartDates: IEkonomiskAnalysDates,
  whatDate: string,
  dateInputType?: DateInputType
): IDate => {
  if (dateInputType === undefined) {
    return date;
  }
  const originalDates = originalStartDates as unknown as Record<
    string,
    { month?: number; day?: number }
  >;
  if (dateInputType === DateInputType.year) {
    date.month = originalDates[whatDate].month ?? 1;
    date.day = originalDates[whatDate].day ?? 1;
  }
  if (dateInputType === DateInputType.yearMonth) {
    date.day = originalDates[whatDate].day ?? 1;
  }

  return date;
};

/** @deprecated */
export const updateDatesByDateInputType = (
  dates: IEkonomiskAnalysDates,
  originalStartDates: IEkonomiskAnalysDates,
  dateInputType?: DateInputType
): IEkonomiskAnalysDates => {
  const from = updateDateByDateInputType(
    dates.from,
    originalStartDates,
    'from',
    dateInputType
  );
  const to = updateDateByDateInputType(
    dates.to,
    originalStartDates,
    'to',
    dateInputType
  );
  return { from, to };
};

/** @deprecated */
export const dateInputTypeWasRaised = (
  oldType?: DateInputType,
  newType?: DateInputType
): boolean => {
  return oldType !== undefined && newType !== undefined && oldType < newType;
};

/**
 * Returns date in the format: YYYY-MM-DD
 */
/** @deprecated */
export const formatDate = (date: Date): string => {
  let convertDate = date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  return convertDate;
};

/**
 * Returns time in the format: HH:NN
 */
/** @deprecated */
export const formatTime = (date: Date): string => {
  let convertDate = date.toLocaleString('sv-SE', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return convertDate;
};

/**
 * Returns date and time in the format: YYYY-MM-DD HH:NN
 */
/** @deprecated */
export const formatDateTime = (date: Date): string => {
  let convertDate = date.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });

  return convertDate;
};

/** @deprecated */
export const getDaysInMonth = (year: number, month: number) =>
  new Date(year, month, 0).getDate();

/**
 * Creates a date from given year, month and day.
 * @param {number} year - The year.
 * @param {number} month - The month.
 * @param {number} day - The day.
 * @returns {Date | undefined} The date if encoding is successful, undefined otherwise.
 */
export const tryEncodeDate = (
  year: number,
  month: number,
  day: number
): Date | undefined => {
  if (
    year >= MIN_YEAR &&
    year <= MAX_YEAR &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= 31 &&
    day <= daysInMonth2(year, month)
  ) {
    return new Date(year, month - 1, day);
  } else {
    return undefined;
  }
};

/**
 * Creates a date from given year, month and day.
 * @param {number} year - The year.
 * @param {number} month - The month.
 * @param {number} day - The day.
 * @returns {Date} The date if encoding is successful, undefined otherwise.
 */
export const safeEncodeDate = (
  year: number,
  month: number,
  day: number
): Date => {
  year = clamp(MIN_YEAR, MAX_YEAR, year);
  month = clamp(1, 12, month);
  day = clamp(1, daysInMonth2(year, month), day);
  return new Date(year, month - 1, day);
};

export const capitalizeFirstLetter = (day: string) => {
  return day.charAt(0).toUpperCase() + day.slice(1);
};

/**
 * Creates a time from given hour, minute, second and millisecond.
 * @param {number} hour - The hour.
 * @param {number} minute - The minute.
 * @param {number?} second - The second.
 * @param {number?} millisecond - The millisecond.
 * @returns {Date | undefined} The time if encoding is successful, undefined otherwise.
 */
export const tryEncodeTime = (
  hour: number,
  minute: number,
  second?: number,
  millisecond?: number
): Date | undefined => {
  if (
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59 &&
    (!second || (second >= 0 && second <= 59)) &&
    (!millisecond || (millisecond >= 0 && millisecond <= 999))
  ) {
    return new Date(1970, 0, 1, hour, minute, second, millisecond);
  } else {
    return undefined;
  }
};

/**
 * Creates a date from given year, month and day.
 * @param {number} year - The year.
 * @param {number} month - The month.
 * @param {number} day - The day.
 * @param {number} hour - The hour.
 * @param {number} minute - The minute.
 * @param {number?} second - The second.
 * @param {number?} millisecond - The millisecond.
 * @returns {Date | undefined} The date if encoding is successful, undefined otherwise.
 */
export const tryEncodeDateTime = (
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  second?: number,
  millisecond?: number
): Date | undefined => {
  if (
    year >= MIN_YEAR &&
    year <= MAX_YEAR &&
    month >= 1 &&
    month <= 12 &&
    day >= 1 &&
    day <= 31 &&
    day <= daysInMonth2(year, month) &&
    hour >= 0 &&
    hour <= 23 &&
    minute >= 0 &&
    minute <= 59 &&
    (!second || (second >= 0 && second <= 59)) &&
    (!millisecond || (millisecond >= 0 && millisecond <= 999))
  ) {
    return new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second ?? 0,
      millisecond ?? 0
    );
  } else {
    return undefined;
  }
};

/**
 * Extracts the date part of a date/time.
 * @param {Date} dateTime - The date/time value, or current date if omitted.
 * @returns {Date} The date part of the given date/time value.
 */
export const getDate = (dateTime?: Date): Date => {
  return dateFns.startOfDay(dateTime ?? new Date());
};

/**
 * Extracts the time part of a date/time.
 * @param {Date} dateTime - The date/time value, or current time if omitted.
 * @returns {Date} The time part of the given date/time value.
 */
export const getTime = (dateTime?: Date): Date => {
  return dateFns.setDate(
    dateFns.setMonth(dateFns.setYear(dateTime ?? new Date(), 1970), 0),
    1
  );
};

/**
 * Extracts the century part of a date.
 * @param {Date} dateTime - The date value.
 * @returns {number} The century part of the given date.
 */
export const getCentury = (dateTime: Date): number => {
  const year = getYear(dateTime);
  return Math.floor(year / 100);
};

/**
 * Extracts the year part of a date.
 * @param {Date} dateTime - The date value.
 * @returns {number} The year part of the given date.
 */
export const getYear = (dateTime: Date): number => {
  return dateFns.getYear(dateTime);
};

/**
 * Extracts the month part of a date.
 * @param {Date} dateTime - The date value.
 * @returns {number} The month part of the given date.
 */
export const getMonth = (dateTime: Date): number => {
  return dateFns.getMonth(dateTime) + 1;
};

/**
 * Extracts the day part of a date.
 * @param {Date} dateTime - The date value.
 *  {number} The day part of the given date.
 */
export const getDay = (dateTime: Date): number => {
  return dateFns.getDate(dateTime);
};

/**
 * Extracts the year and month parts of a date.
 * @param {Date} dateTime - The date value.
 * @param {number} Year - Output parameter for the year part.
 * @param {number} Month - Output parameter for the month part.
 */
// export const getYearMonth = (dateTime: Date): [number, number] => {
//   const year = getYear(dateTime);
//   const month = getMonth(dateTime) + 1;
//   return [year, month];
// };

/**
 * Extracts the year, month, and day parts of a date.
 * @param {Date} dateTime - The date value.
 * @returns {[number, number, number]} The year, month and day part.
 */
export const getYearMonthDay = (dateTime: Date): [number, number, number] => {
  const year = getYear(dateTime);
  const month = getMonth(dateTime);
  const day = getDay(dateTime);
  return [year, month, day];
};

/**
 * Extracts the year and quarter parts of a date.
 * @param {Date} date - The date value.
 * @returns {string} The year and quarter as text.
 */
export const getYearQuarter = (date: Date): string => {
  return `${getYear(date).toString().padStart(4, '0')}-${getQuarter2(date)}`;
};

/**
 * Calculates the quarter of a month.
 * @param {number} month - The date value.
 * @returns {number} The quarter value (1 - 4).
 */
export const getQuarter = (month: number): number => {
  if (month >= 1 && month <= 12) {
    return Math.ceil(month / 3);
  } else {
    throw namedError('InvalidMonth', `Invalid month: "${month}"`);
  }
};

/**
 * Calculates the quarter of a date.
 * @param {Date} date - The date value.
 * @returns {number} The quarter value (1 - 4).
 */
export const getQuarter2 = (date: Date): number => {
  const month = date.getMonth() + 1;
  return getQuarter(month);
};

/**
 * Calculates the first month of a quarter.
 * @param {number} quarter - The quarter value (1 - 4).
 * @returns {number} The month value of the quarter (1, 4, 7 or 10).
 */
export const getQuarterMonth = (quarter: number): number => {
  if (quarter >= 1 && quarter <= 4) {
    const month = quarter * 3 - 2;
    return month;
  } else {
    throw namedError('InvalidQuarter', `Invalid quarter: "${quarter}"`);
  }
};

/**
 * Calculates the first date of a quarter.
 * @param {number} year - The year.
 * @param {number} quarter - The quarter value (0 - 3).
 */
export const getQuarterDate = (year: number, quarter: number): Date => {
  if (year >= MIN_YEAR && year <= MAX_YEAR) {
    const date = new Date(year, getQuarterMonth(quarter) - 1, 1);
    return date;
  } else {
    throw namedError('InvalidYear', `Invalid year: "${year}"`);
  }
};

/**
 * Extracts the hour part of a time.
 * @param {Date} dateTime - The time value.
 * @returns {number} The hour part of the given time.
 */
export const getHour = (dateTime: Date): number => {
  return dateFns.getHours(dateTime);
};

/**
 * Extracts the minutes part of a time.
 * @param {Date} dateTime - The time value.
 * @returns {number} The minutes part of the given time.
 */
export const getMin = (dateTime: Date): number => {
  return dateFns.getMinutes(dateTime);
};

/**
 * Extracts the seconds part of a time.
 * @param {Date} dateTime - The time value.
 * @returns {number} The seconds part of the given time.
 */
export const getSec = (dateTime: Date): number => {
  return dateFns.getSeconds(dateTime);
};

/**
 * Extracts the milliseconds part of a time.
 * @param {Date} dateTime - The time value.
 * @returns {number} The milliseconds part of the given time.
 */
export const getMSec = (dateTime: Date): number => {
  return dateFns.getMilliseconds(dateTime);
};

/**
 * Extracts the hour and minute parts of a time.
 * @param {Date} dateTime - The time value.
 * @returns Hour and minutes.
 */
export const getHourMin = (dateTime: Date): [number, number] => {
  const hour = getHour(dateTime);
  const min = getMin(dateTime);
  return [hour, min];
};

/**
 * Sets the date part of the first argument to that of the second.
 * @param {Date} dateTime - The original date/time value.
 * @param {Date} date - The new date value.
 * @returns {Date} The modified date/time value with the updated date part.
 */
export const setDate = (dateTime: Date, date: Date): Date => {
  return setYear(
    setMonth(setDay(dateTime, getDay(date)), getMonth(date)),
    getYear(date)
  );
};

/**
 * Sets the time part of the first argument to that of the second.
 * @param {Date} dateTime - The original date/time value.
 * @param {Date} time - The new time value.
 * @returns {Date} The modified date/time value with the updated time part.
 */
export const setTime = (dateTime: Date, time: Date): Date => {
  return setMSec(
    setSec(
      setMin(setHour(dateTime, getHour(time)), getMin(time)),
      getSec(time)
    ),
    getMSec(time)
  );
};

/**
 * Sets the year part of a date.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} year - The new year value.
 * @returns {Date} The modified date/time value with the updated year part.
 */
export const setYear = (dateTime: Date, year: number): Date => {
  return dateFns.setYear(dateTime, year);
};

/**
 * Sets the month part of a date.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} month - The new month value.
 * @returns {Date} The modified date/time value with the updated month part.
 */
export const setMonth = (dateTime: Date, month: number): Date => {
  return dateFns.setMonth(dateTime, month - 1);
};

/**
 * Sets the day part of a date.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} day - The new day value.
 * @returns {Date} The modified date/time value with the updated day part.
 */
export const setDay = (dateTime: Date, day: number): Date => {
  return dateFns.setDate(dateTime, day);
};

/**
 * Sets the hour part of a time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} hour - The new hour value.
 * @returns {Date} The modified date/time value with the updated hour part.
 */
export const setHour = (dateTime: Date, hour: number): Date => {
  return dateFns.setHours(dateTime, hour);
};

/**
 * Sets the minutes part of a time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} min - The new minutes value.
 * @returns {Date} The modified date/time value with the updated minutes part.
 */
export const setMin = (dateTime: Date, min: number): Date => {
  return dateFns.setMinutes(dateTime, min);
};

/**
 * Sets the seconds part of a time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} sec - The new seconds value.
 * @returns {Date} The modified date/time value with the updated seconds part.
 */
export const setSec = (dateTime: Date, sec: number): Date => {
  return dateFns.setSeconds(dateTime, sec);
};

/**
 * Sets the milliseconds part of a time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} milliSeconds - The new milliseconds value.
 * @returns {Date} The modified date/time value with the updated milliseconds part.
 */
export const setMSec = (dateTime: Date, milliSeconds: number): Date => {
  return dateFns.setMilliseconds(dateTime, milliSeconds);
};

/**
 * Clears the day and time parts of the given date/time.
 * @param {Date} dateTime - The original date/time value.
 * @returns {Date} The modified date/time value with the day and time parts cleared.
 */
export const truncToMonth = (dateTime: Date): Date => {
  return dateFns.startOfMonth(dateTime);
};

/**
 * Clears the time part of the given date/time.
 * @param {Date} dateTime - The original date/time value.
 * @returns {Date} The modified date/time value with the time part cleared.
 */
export const truncToDay = (dateTime: Date): Date => {
  return dateFns.startOfDay(dateTime);
};

/**
 * Clears the minute, second, and millisecond parts of the given date/time.
 * @param {Date} dateTime - The original date/time value.
 * @returns {Date} The modified date/time value with the minute, second, and millisecond parts cleared.
 */
export const truncToHour = (dateTime: Date): Date => {
  return dateFns.startOfHour(dateTime);
};

/**
 * Clears the second and millisecond parts of the given date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [minuteMultiple=1] - The minute multiple (default: 1).
 * @returns {Date} The modified date/time value with the second and millisecond parts cleared.
 */
export const truncToMin = (
  dateTime: Date,
  minuteMultiple: number = 1
): Date => {
  let truncatedDateTime = dateFns.startOfMinute(dateTime);
  if (minuteMultiple > 1) {
    const minutes = getMin(truncatedDateTime);
    const roundedMinutes =
      Math.floor(minutes / minuteMultiple) * minuteMultiple;
    truncatedDateTime = setMin(truncatedDateTime, roundedMinutes);
  }
  return truncatedDateTime;
};

/**
 * Clears the millisecond parts of the given date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [secondMultiple=1] - The second multiple (default: 1).
 * @returns {Date} The modified date/time value with the millisecond parts cleared.
 */
export const truncToSec = (
  dateTime: Date,
  secondMultiple: number = 1
): Date => {
  let truncatedDateTime = dateFns.startOfSecond(dateTime);
  if (secondMultiple > 1) {
    const seconds = getSec(truncatedDateTime);
    const roundedSeconds =
      Math.floor(seconds / secondMultiple) * secondMultiple;
    truncatedDateTime = setSec(truncatedDateTime, roundedSeconds);
  }
  return truncatedDateTime;
};

/**
 * Adds years to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [years=1] - The number of years to add (default: 1).
 * @returns {Date} The modified date/time value with the added years.
 */
export const incYear = (dateTime: Date, years: number = 1): Date => {
  let dateTimeWithAddedYears = dateFns.addYears(dateTime, years);
  dateTimeWithAddedYears = dateFns.min([dateTimeWithAddedYears, MAX_DATE_TIME]);
  dateTimeWithAddedYears = dateFns.max([dateTimeWithAddedYears, MIN_DATE_TIME]);

  return dateTimeWithAddedYears;
};

/**
 * Adds months to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [months=1] - The number of months to add (default: 1).
 * @returns {Date} The modified date/time value with the added months.
 */
export const incMonth = (dateTime: Date, months: number = 1): Date => {
  let dateTimeWithAddedMonths = dateFns.addMonths(dateTime, months);
  dateTimeWithAddedMonths = dateFns.min([
    dateTimeWithAddedMonths,
    MAX_DATE_TIME,
  ]);
  dateTimeWithAddedMonths = dateFns.max([
    dateTimeWithAddedMonths,
    MIN_DATE_TIME,
  ]);

  return dateTimeWithAddedMonths;
};

/**
 * Adds days to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [days=1] - The number of days to add (default: 1).
 * @returns {Date} The modified date/time value with the added days.
 */
export const incDay = (dateTime: Date, days: number = 1): Date => {
  let dateTimeWithAddedDays = dateFns.addDays(dateTime, days);
  dateTimeWithAddedDays = dateFns.min([dateTimeWithAddedDays, MAX_DATE_TIME]);
  dateTimeWithAddedDays = dateFns.max([dateTimeWithAddedDays, MIN_DATE_TIME]);

  return dateTimeWithAddedDays;
};

/**
 * Adds hours to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [hours=1] - The number of hours to add (default: 1).
 * @returns {Date} The modified date/time value with the added hours.
 */
export const incHour = (dateTime: Date, hours: number = 1): Date => {
  let dateTimeWithAddedHours = dateFns.addHours(dateTime, hours);
  dateTimeWithAddedHours = dateFns.min([dateTimeWithAddedHours, MAX_DATE_TIME]);
  dateTimeWithAddedHours = dateFns.max([dateTimeWithAddedHours, MIN_DATE_TIME]);

  return dateTimeWithAddedHours;
};

/**
 * Adds minutes to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [mins=1] - The number of minutes to add (default: 1).
 * @returns {Date} The modified date/time value with the added minutes.
 */
export const incMin = (dateTime: Date, mins: number = 1): Date => {
  let dateTimeWithAddedMinutes = dateFns.addMinutes(dateTime, mins);
  dateTimeWithAddedMinutes = dateFns.min([
    dateTimeWithAddedMinutes,
    MAX_DATE_TIME,
  ]);
  dateTimeWithAddedMinutes = dateFns.max([
    dateTimeWithAddedMinutes,
    MIN_DATE_TIME,
  ]);

  return dateTimeWithAddedMinutes;
};

/**
 * Creates a date from a base date with specific hour and minute, using local time.
 * DST-safe alternative to incMin from midnight.
 * @param {Date} date - The base date (only year, month, day used).
 * @param {number} hour - The hour (0-23).
 * @param {number} minute - The minute (0-59).
 * @returns {Date} The date with the given local time.
 */
export const dateAtTime = (date: Date, hour: number, minute: number): Date => {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    hour,
    minute,
    0,
    0
  );
};

/**
 * Adds seconds to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [secs=1] - The number of seconds to add (default: 1).
 * @returns {Date} The modified date/time value with the added seconds.
 */
export const incSec = (dateTime: Date, secs: number = 1): Date => {
  let dateTimeWithAddedSeconds = dateFns.addSeconds(dateTime, secs);
  dateTimeWithAddedSeconds = dateFns.min([
    dateTimeWithAddedSeconds,
    MAX_DATE_TIME,
  ]);
  dateTimeWithAddedSeconds = dateFns.max([
    dateTimeWithAddedSeconds,
    MIN_DATE_TIME,
  ]);

  return dateTimeWithAddedSeconds;
};

/**
 * Adds milliseconds to a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [milliseconds=1] - The number of milliseconds to add (default: 1).
 * @returns {Date} The modified date/time value with the added milliseconds.
 */
export const incMSec = (dateTime: Date, milliseconds: number = 1): Date => {
  let dateTimeWithAddedMilliseconds = dateFns.addMilliseconds(
    dateTime,
    milliseconds
  );
  dateTimeWithAddedMilliseconds = dateFns.min([
    dateTimeWithAddedMilliseconds,
    MAX_DATE_TIME,
  ]);
  dateTimeWithAddedMilliseconds = dateFns.max([
    dateTimeWithAddedMilliseconds,
    MIN_DATE_TIME,
  ]);

  return dateTimeWithAddedMilliseconds;
};

/**
 * Subtracts years from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [years=1] - The number of years to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted years.
 */
export const decYear = (dateTime: Date, years: number = 1): Date => {
  return incYear(dateTime, -years);
};

/**
 * Subtracts months from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [months=1] - The number of months to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted months.
 */
export const decMonth = (dateTime: Date, months: number = 1): Date => {
  return incMonth(dateTime, -months);
};

/**
 * Subtracts days from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [days=1] - The number of days to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted days.
 */
export const decDay = (dateTime: Date, days: number = 1): Date => {
  return incDay(dateTime, -days);
};

/**
 * Subtracts hours from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [hours=1] - The number of hours to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted hours.
 */
export const decHour = (dateTime: Date, hours: number = 1): Date => {
  return incHour(dateTime, -hours);
};

/**
 * Subtracts minutes from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [mins=1] - The number of minutes to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted minutes.
 */
export const decMin = (dateTime: Date, mins: number = 1): Date => {
  return incMin(dateTime, -mins);
};

/**
 * Subtracts seconds from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [secs=1] - The number of seconds to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted seconds.
 */
export const decSec = (dateTime: Date, secs: number = 1): Date => {
  return incSec(dateTime, -secs);
};

/**
 * Subtracts milliseconds from a date/time.
 * @param {Date} dateTime - The original date/time value.
 * @param {number} [milliseconds=1] - The number of milliseconds to subtract (default: 1).
 * @returns {Date} The modified date/time value with the subtracted milliseconds.
 */
export const decMSec = (dateTime: Date, milliseconds: number = 1): Date => {
  return incMSec(dateTime, -milliseconds);
};

/**
 * Returns the difference in whole years between two dates.
 * @param {Date} dateFrom - The starting date.
 * @param {Date} dateTo - The ending date.
 * @returns {number} The difference in whole years.
 */
export const diffYear = (dateFrom: Date, dateTo: Date): number => {
  return dateFns.differenceInYears(dateTo, dateFrom);
};

/**
 * Returns the difference in whole years between two dates. Ignores day, month, and time values.
 * @param {Date} dateFrom - The starting date.
 * @param {Date} dateTo - The ending date.
 * @returns {number} The difference in whole years.
 */
export const diffYear2 = (dateFrom: Date, dateTo: Date): number => {
  return getYear(dateTo) - getYear(dateFrom);
};

/**
 * Returns the difference in whole months between two dates.
 * @param {Date} dateFrom - The starting date.
 * @param {Date} dateTo - The ending date.
 * @returns {number} The difference in whole months.
 */
export const diffMonth = (dateFrom: Date, dateTo: Date): number => {
  return dateFns.differenceInMonths(dateTo, dateFrom);
};

/**
 * Returns the difference in whole months between two dates. Ignores day and time values.
 * @param {Date} dateFrom - The starting date.
 * @param {Date} dateTo - The ending date.
 * @returns {number} The difference in whole months.
 */
export const diffMonth2 = (dateFrom: Date, dateTo: Date): number => {
  const diff =
    (getYear(dateTo) - getYear(dateFrom)) * 12 +
    getMonth(dateTo) -
    getMonth(dateFrom);
  return diff;
};

/**
 * Returns the difference in whole days between two dates.
 * @param {Date} dateFrom - The starting date.
 * @param {Date} dateTo - The ending date.
 * @returns {number} The difference in whole days.
 */
export const diffDay = (dateFrom: Date, dateTo: Date): number => {
  return dateFns.differenceInDays(dateTo, dateFrom);
};

/**
 * Returns the difference in whole days between two dates. Ignores time values.
 * @param {Date} dateFrom - The starting date.
 * @param {Date} dateTo - The ending date.
 * @returns {number} The difference in whole days.
 */
export const diffDay2 = (dateFrom: Date, dateTo: Date): number => {
  return dateFns.differenceInDays(getDate(dateTo), getDate(dateFrom));
};

/**
 * Returns the difference in whole hours between two times.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in whole hours.
 */
export const diffHour = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInHours(timeTo, timeFrom);
};

/**
 * Returns the difference in whole hours between two times. Ignores minute, second, and millisecond values.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in whole hours.
 */
export const diffHour2 = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInHours(truncToHour(timeTo), truncToHour(timeFrom));
};

/**
 * Returns the difference in whole minutes between two times.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in whole minutes.
 */
export const diffMin = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInMinutes(timeTo, timeFrom);
};

/**
 * Returns the difference in whole minutes between two times. Ignores second and millisecond values.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in whole minutes.
 */
export const diffMin2 = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInMinutes(truncToMin(timeTo), truncToMin(timeFrom));
};

/**
 * Returns the difference in whole seconds between two times.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in whole seconds.
 */
export const diffSec = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInSeconds(timeTo, timeFrom);
};

/**
 * Returns the difference in whole seconds between two times. Ignores millisecond values.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in whole seconds.
 */
export const diffSec2 = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInSeconds(truncToSec(timeTo), truncToSec(timeFrom));
};

/**
 * Returns the difference in milliseconds between two times.
 * @param {Date} timeFrom - The starting time.
 * @param {Date} timeTo - The ending time.
 * @returns {number} The difference in milliseconds.
 */
export const diffMSec = (timeFrom: Date, timeTo: Date): number => {
  return dateFns.differenceInMilliseconds(timeTo, timeFrom);
};

/**
 * Compares two datetimes.
 * @param {Date} dateTime1 - The first datetime.
 * @param {Date} dateTime2 - The second datetime.
 * @returns {number} -1 if DateTime1 is earlier than DateTime2, 1 if it is later, 0 if they are equal.
 */
export const compareDateTime = (dateTime1: Date, dateTime2: Date): number => {
  return dateFns.compareAsc(dateTime1, dateTime2);
};

/**
 * Compares two dates.
 * @param {Date} date1 - The first date.
 * @param {Date} date2 - The second date.
 * @returns {number} -1 if Date1 is earlier than Date2, 1 if it is later, 0 if they are equal.
 */
export const compareDatePart = (date1: Date, date2: Date): number => {
  return dateFns.compareAsc(getDate(date1), getDate(date2));
};

/**
 * Compares two times.
 * @param {Date} time1 - The first time.
 * @param {Date} time2 - The second time.
 * @returns {number} -1 if Time1 is earlier than Time2, 1 if it is later, 0 if they are equal.
 */
export const compareTimePart = (time1: Date, time2: Date): number => {
  return dateFns.compareAsc(getTime(time1), getTime(time2));
};

/**
 * Returns the number of days in the given month.
 * @param {Date} date - The date representing the month.
 * @returns {number} The number of days in the month.
 */
export const daysInMonth = (date: Date): number => {
  return dateFns.getDaysInMonth(date);
};

/**
 * Returns the number of days in the given month.
 * @param {number} year - The year.
 * @param {number} month - The month (1-12).
 * @returns {number} The number of days in the month.
 */
export const daysInMonth2 = (year: number, month: number): number => {
  const date = new Date(year, month - 1); // Adjust month to 0-based index
  return daysInMonth(date);
};

/**
 * Returns the first day of the week in the given date.
 * @param {Date} date - The date.
 * @returns {Date} The first day of the week.
 */
export const firstDayInWeek = (date: Date): Date => {
  const firstDayInWeek = dateFns.startOfWeek(date);
  return dateFns.max([firstDayInWeek, MIN_DATE]);
};

/**
 * Returns the last day of the week in the given date.
 * @param {Date} date - The date.
 * @returns {Date} The last day of the week.
 */
export const lastDayInWeek = (date: Date): Date => {
  const lastDayInWeek = getDate(dateFns.endOfWeek(date));
  return dateFns.min([lastDayInWeek, MAX_DATE]);
};

/**
 * Returns the first day of the month in the given date.
 * @param {Date} date - The date.
 * @returns {Date} The first day of the month.
 */
export const firstDayInMonth = (date: Date): Date => {
  return dateFns.startOfMonth(date);
};

/**
 * Returns the last day of the month in the given date.
 * @param {Date} date - The date.
 * @returns {Date} The last day of the month.
 */
export const lastDayInMonth = (date: Date): Date => {
  return getDate(dateFns.endOfMonth(date));
};

/**
 * Returns the first day of the year in the given date.
 * @param {Date} date - The date.
 * @returns {Date} The first day of the year.
 */
export const firstDayInYear = (date: Date): Date => {
  return dateFns.startOfYear(date);
};

/**
 * Returns the last day of the year in the given date.
 * @param {Date} date - The date.
 * @returns {Date} The last day of the year.
 */
export const lastDayInYear = (date: Date): Date => {
  return getDate(dateFns.endOfYear(date));
};

/**
 * Returns the year and week number of a given date.
 * @param {Date} date - The date.
 * @returns An array with year and week number.
 */
export const dateToWeekNo = (date: Date): [number, number] => {
  const week = dateFns.getISOWeek(date);
  const year = dateFns.getISOWeekYear(date);

  return [year, week];
};

/**
 * Returns the date of the first day (Monday) in the given week.
 * @param {number} year - The year.
 * @param {number} weekNo - The week number.
 * @returns {Date} The date of the first day in the week.
 */
export const weekNoToDate = (year: number, weekNo: number): Date => {
  const startDateOfWeek = dateFns.startOfWeek(new Date(year, 1, 31));
  const date = dateFns.setISOWeek(startDateOfWeek, weekNo);
  return date;
};

/**
 * Returns the week number of a given date.
 * @param {Date} date - The date.
 * @returns {number} The week number.
 */
export const weekNo = (date: Date): number => {
  return dateFns.getISOWeek(date);
};

/**
 * Returns the day number of a given date.
 * @param {Date} date - The date.
 * @returns {number} The day number.
 */
export const dayNo = (date: Date): number => {
  return dateFns.getDayOfYear(date);
};

/**
 * Returns the number of weeks in the given year.
 * @param {number} year - The year.
 * @returns {number} The number of weeks in the year.
 */
export const weeksInYear = (year: number): number => {
  return dateFns.getISOWeeksInYear(new Date(year, 1, 30));
};

/**
 * Returns the date of a weekday (Monday - Sunday) in the week of a given date.
 * @param {Date} date - The date.
 * @param {number} weekDay - The weekday (0 = Monday, 1 = Tuesday, ..., 6 = Sunday).
 * @returns {Date} The date of the weekday in the week.
 */
export const getWeekDayInWeek = (date: Date, weekDay: WeekDay): Date => {
  const firstDayOfWeek = dateFns.startOfWeek(date);
  const weekDayInWeek = dateFns.addDays(firstDayOfWeek, weekDay);
  if (weekDayInWeek > MAX_DATE_TIME) {
    throw namedError('InvalidWeekDay', 'Invalid week day');
  }
  return weekDayInWeek;
};

export const convertParams_getWeekDayInWeek = (
  params: [string, string]
): [Date, WeekDay] => {
  const weekDayIndex = WEEKDAYNAMES_SHORT.findIndex(name => name === params[1]);

  return [dateFns.parseISO(params[0]), weekDayIndex];
};

/**
 * Returns the date of a weekday (Monday - Sunday) in a given week.
 * @param {number} year - The year.
 * @param {number} weekNo - The week number.
 * @param {number} weekDay - The weekday (0 = Monday, 1 = Tuesday, ..., 6 = Sunday).
 * @returns {Date} The date of the weekday in the week.
 */
export const getWeekDayInWeek2 = (
  year: number,
  weekNo: number,
  weekDay: number
): Date => {
  const date = weekNoToDate(year, weekNo);
  return getWeekDayInWeek(date, weekDay);
};

export const convertParams_getWeekDayInWeek2 = (
  params: [number, number, string]
): [number, number, WeekDay] => {
  const weekDayIndex = WEEKDAYNAMES_SHORT.findIndex(name => name === params[2]);

  return [params[0], params[1], weekDayIndex];
};

/**
 * Returns the weekday (Monday - Sunday).
 * @param {Date} ADate - The date.
 * @returns {number} The weekday (0 = Monday, 1 = Tuesday, ..., 6 = Sunday).
 */
export const getWeekDay = (ADate: Date): WeekDay => {
  return dateFns.getISODay(ADate) - 1;
};

export const convertExpected_getWeekDay = (expected: string): WeekDay => {
  const weekDayIndex = WEEKDAYNAMES_SHORT.findIndex(name => name === expected);

  return weekDayIndex;
};

/**
 * Returns the next occurrence of a given weekday (Monday - Sunday) starting from a given date.
 * @param {Date} fromDate - The starting date.
 * @param {number} weekDay - The weekday (0 = Monday, 1 = Tuesday, ..., 6 = Sunday).
 * @returns {Date} The next occurrence of the weekday.
 */
export const getNextWeekDayFromDate = (
  fromDate: Date,
  weekDay: WeekDay
): Date => {
  const dayOfWeek = getWeekDay(fromDate);
  const daysToAdd = weekDay - dayOfWeek;
  const newDateTime = dateFns.addDays(
    fromDate,
    daysToAdd < 0 ? daysToAdd + 7 : daysToAdd
  );
  if (newDateTime > MAX_DATE_TIME) {
    throw namedError('InvalidWeekDay', `Invalid week day: "${weekDay}"`);
  }
  const date = getDate(newDateTime);
  return date;
};

export const convertParams_getNextWeekDayFromDate = (
  params: [string, string]
): [Date, WeekDay] => {
  const weekDayIndex = WEEKDAYNAMES_SHORT.findIndex(name => name === params[1]);

  return [dateFns.parseISO(params[0]), weekDayIndex];
};

/**
 * Returns the name of the given month.
 * @param {number} month - The month (1 = January, 2 = February, ..., 12 = December).
 * @param {boolean} [shortText=false] - Whether to return the abbreviated month name.
 * @returns {string} The name of the month.
 */
export const getMonthText = (
  month: number,
  shortText: boolean = false
): string => {
  if (month >= 1 && month <= 12)
    if (shortText) return MONTHNAMES_SHORT[month - 1];
    else return MONTHNAMES[month - 1];
  else {
    throw namedError('InvalidMonth', `Invalid month: "${month}"`);
  }
};

/**
 * Returns the name of the month of a given date.
 * @param {Date} date - The date.
 * @param {boolean} [shortText=false] - Whether to return the abbreviated month name.
 * @returns {string} The name of the month.
 */
export const getMonthText2 = (
  date: Date,
  shortText: boolean = false
): string => {
  const month = date.getMonth() + 1;
  return getMonthText(month, shortText);
};

/**
 * Returns the name of the given weekday (Monday - Sunday).
 * @param {number} weekDay - The weekday (0 = Monday, 1 = Tuesday, ..., 6 = Sunday).
 * @param {boolean} [shortText=false] - Whether to return the abbreviated weekday name.
 * @returns {string} The name of the weekday.
 */
export const getWeekDayText = (
  weekDay: number,
  shortText: boolean = false
): string => {
  if (weekDay >= 0 && weekDay <= 6)
    if (shortText) {
      return WEEKDAYNAMES_SHORT[weekDay];
    } else {
      return WEEKDAYNAMES[weekDay];
    }
  else {
    throw namedError('InvalidWeekDay', `Invalid week day: ${weekDay}`);
  }
};
export const convertParams_getWeekDayText = (
  params: [string, boolean]
): [number, boolean] => {
  const [weekDay, shortText] = params;
  return [WEEKDAYNAMES_SHORT.findIndex(wd => wd === weekDay), shortText];
};

/**
 * Returns the name of the weekday (Monday - Sunday) of a given date.
 * @param {Date} date - The date.
 * @param {boolean} [shortText=false] - Whether to return the abbreviated weekday name.
 * @returns {string} The name of the weekday.
 */
export const getWeekDayText2 = (
  date: Date,
  shortText: boolean = false
): string => {
  const weekday = getWeekDay(date);
  return getWeekDayText(weekday, shortText);
};

/**
 * Returns the name of the given quarter.
 * @param {number} month - The month (1 = January, 2 = February, ..., 12 = December).
 * @returns {string} The name of the quarter.
 */
export const getQuarterText = (month: number): string => {
  if (month >= 1 && month <= 12) {
    const quarter = getQuarter(month);
    return QUARTERNAMES[quarter - 1];
  } else {
    throw namedError('InvalidMonth', `Invalid month: "${month}"`);
  }
};

/**
 * Returns the name of the quarter of a given date.
 * @param {Date} date - The date.
 * @returns {string} The name of the quarter.
 */
export const getQuarterText2 = (date: Date): string => {
  const month = date.getMonth() + 1;
  return getQuarterText(month);
};

/**
 * Converts a text representation of a month to its corresponding number.
 * @param {string} text - The text representation of the month.
 * @returns {number} The corresponding month number (1 = January, 2 = February, ..., 12 = December).
 */
export const textToMonth = (text: string): number => {
  let index =
    MONTHNAMES_SHORT.findIndex(
      monthName => monthName.toLowerCase() === text.toLowerCase()
    ) + 1;
  if (index < 1)
    index =
      MONTHNAMES.findIndex(
        monthName => monthName.toLowerCase() === text.toLowerCase()
      ) + 1;
  if (index < 1) {
    throw namedError('InvalidMonth', `Invalid month: "${text}"`);
  }
  return index;
};

/**
 * Converts a text representation of a weekday to its corresponding number.
 * @param {string} text - The text representation of the weekday.
 * @returns {number} The corresponding weekday number (0 = Monday, 1 = Tuesday, ..., 6 = Sunday).
 */
export const textToWeekDay = (text: string): number => {
  let index = WEEKDAYNAMES_SHORT.findIndex(
    weekDayName => weekDayName.toLowerCase() === text.toLowerCase()
  );
  if (index < 0)
    index = WEEKDAYNAMES.findIndex(
      weekDayName => weekDayName.toLowerCase() === text.toLowerCase()
    );
  if (index < 0) {
    throw namedError('InvalidWeekDay', `Invalid week day: "${text}"`);
  }
  return index;
};

/**
 * Converts a text representation of a quarter to its corresponding number.
 * @param {string} text - The text representation of the quarter.
 * @returns {number} The corresponding quarter number.
 */
export const textToQuarter = (text: string): number => {
  let index =
    QUARTERNAMES.findIndex(
      quarterName => quarterName.toLowerCase() === text.toLowerCase()
    ) + 1;
  if (index < 1) {
    throw namedError('InvalidQuarter', `Invalid quarter: "${text}"`);
  }
  return index;
};

/**
 * Creates a date string from given year, month and day.
 * @param {number | undefined} year - The year.
 * @param {number | undefined} month - The month.
 * @param {number | undefined} day - The day.
 * @returns {string | undefined} The date string if encoding is successful, undefined otherwise.
 */
export const tryEncodeDateString = (
  year: number | undefined,
  month: number | undefined,
  day: number | undefined
): string | undefined => {
  const y = year ?? -1;
  const m = month ?? -1;
  const d = day ?? -1;
  if (
    y <= MAX_YEAR &&
    m <= 12 &&
    m <= 31 &&
    (y >= 0 || (m < 0 && d < 0)) &&
    (m >= 0 || d < 0) &&
    (y >= MIN_YEAR || (m < 1 && d < 1)) &&
    (m >= 1 || d < 1) &&
    (d <= 28 || d <= daysInMonth2(y, m))
  ) {
    let result = '';
    if (y >= 0) {
      result = result + y.toString().padStart(4, '0');
      if (m >= 0) {
        result = result + m.toString().padStart(2, '0');
        if (d >= 0) result = result + d.toString().padStart(2, '0');
      }
    }
    return result;
  } else {
    return undefined;
  }
};

export const convertParams_tryEncodeDateString = (
  params: [number, number, number]
): [number | undefined, number | undefined, number | undefined] => {
  const [y, m, d] = params;
  return [
    y !== -1 ? y : undefined,
    m !== -1 ? m : undefined,
    d !== -1 ? d : undefined,
  ];
};

export const convertExpected_tryEncodeDateString = (
  expected: [boolean, string]
): string | undefined => {
  const [ok, dateString] = expected;
  return ok ? dateString : undefined;
};

/**
 * Creates a date string from given year, month and day.
 * @param {number | undefined} year - The year.
 * @param {number | undefined} month - The month.
 * @param {number | undefined} day - The day.
 * @param {string} defaultValue - The default value to use if encoding fails.
 * @returns {string} The encoded date string or the default value.
 */
export const encodeDateStringDef = (
  year: number | undefined,
  month: number | undefined,
  day: number | undefined,
  defaultValue: string = ''
): string => {
  const value = tryEncodeDateString(year, month, day);
  return value ?? defaultValue;
};

export const convertParams_encodeDateStringDef = (
  params: [number, number, number, string]
): [number | undefined, number | undefined, number | undefined, string] => {
  const [y, m, d, def] = params;
  return [
    y !== -1 ? y : undefined,
    m !== -1 ? m : undefined,
    d !== -1 ? d : undefined,
    def,
  ];
};

/**
 * Tries to decode a date string to its components (year, month, day). undefined means not present
 * @param {string | Date} dateString - The date string to decode.
 * @param {number | undefined} [defaultMonth] - The default month value if 0 or not present.
 * @param {number | undefined} [defaultDay] - The default day value if 0 or not present.
 * @returns {[number | undefined, number | undefined, number | undefined] | undefined} [year, month, day] if decoding is successful, undefined otherwise.
 */
export const tryDecodeDateString = (
  dateString: string | Date,
  defaultMonth?: number,
  defaultDay?: number
): [number | undefined, number | undefined, number | undefined] | undefined => {
  if (typeof dateString === 'string') {
    const length = dateString.length;
    if ([0, 4, 6, 8].findIndex(n => n === length) >= 0) {
      let y = length >= 4 ? strToInt(dateString.substring(0, 4)) : -1;
      if (isNaN(y)) return undefined;
      let m = length >= 6 ? strToInt(dateString.substring(4, 6)) : -1;
      if (isNaN(m)) return undefined;
      let d = length >= 8 ? strToInt(dateString.substring(6, 8)) : -1;
      if (isNaN(d)) return undefined;
      if (y >= MIN_YEAR && y <= MAX_YEAR && m < 1 && d < 1) {
        m = defaultMonth ?? -1;
        if (m >= 1 && m <= 12 && defaultDay && defaultDay >= 1)
          d = Math.min(defaultDay, daysInMonth2(y, m));
        else d = defaultDay ?? -1;
      }
      if (y >= MIN_YEAR && y <= MAX_YEAR && m >= 1 && m <= 12 && d < 1) {
        if (defaultDay && defaultDay >= 1)
          d = Math.min(defaultDay, daysInMonth2(y, m));
        else d = defaultDay ?? -1;
      }
      if (
        y <= MAX_YEAR &&
        m <= 12 &&
        d <= 31 &&
        (y >= 0 || (m < 0 && d < 0)) &&
        (m >= 0 || d < 0) &&
        (y >= MIN_YEAR || (m < 1 && d < 1)) &&
        (m >= 1 || d < 1) &&
        (d <= 28 || d <= daysInMonth2(y, m))
      ) {
        return [
          y >= 0 ? y : undefined,
          m >= 0 ? m : undefined,
          d >= 0 ? d : undefined,
        ];
      }
    }
    return undefined;
  } else {
    return getYearMonthDay(dateString);
  }
};

export const convertParams_tryDecodeDateString = (
  params: [string, number?, number?]
): [string, number | undefined, number | undefined] => {
  const [dateString, defaultMonth, defaultDay] = params;
  return [
    dateString,
    (defaultMonth ?? -1) >= 0 ? defaultMonth : undefined,
    (defaultDay ?? -1) >= 0 ? defaultDay : undefined,
  ];
};

export const convertExpected_tryDecodeDateString = (
  expected: [boolean, number, number, number]
): [number | undefined, number | undefined, number | undefined] | undefined => {
  const [ok, y, m, d] = expected;
  return ok
    ? [
        y !== -1 ? y : undefined,
        m !== -1 ? m : undefined,
        d !== -1 ? d : undefined,
      ]
    : undefined;
};

/**
 * Converts dates to date strings.
 * @param {Date} date - The date value to convert.
 * @returns {string | undefined} The date string if conversion is successful, undefined otherwise.
 */
export const tryDateToDateString = (
  date: Date | string
): string | undefined => {
  const decoded = tryDecodeDateString(date);
  if (decoded) {
    const [y, m, d] = decoded;
    return tryEncodeDateString(y, m, d);
  } else {
    return undefined;
  }
};
export const convertExpected_tryDateToDateString = (
  expected: [boolean, string?]
): string | undefined => {
  const [ok, date] = expected;
  return ok ? date : undefined;
};

/**
 * Converts dates to date strings.
 * @param {Date | string} date - The date value to convert.
 * @param {string} defaultValue - The default value to use if conversion fails.
 * @returns {string} The converted date string or the default value.
 */
export const dateToDateStringDef = (
  date: Date | string,
  defaultValue: string = ''
): string => {
  const value = tryDateToDateString(date);
  return value ?? defaultValue;
};

/**
 * Tries to convert a date string to a date value.
 * @param {string} dateString - The date string to convert.
 * @param {number?} [defaultMonth] - The default month value if 0 or not present.
 * @param {number?} [defaultDay] - The default day value if 0 or not present.
 * @returns {Date | undefined} Date if conversion is successful, undefined otherwise.
 */
export const tryDateStringToDate = (
  dateString: string,
  defaultMonth: number = 1,
  defaultDay: number = 1
): Date | undefined => {
  const decoded = tryDecodeDateString(dateString, defaultMonth, defaultDay);
  if (decoded) {
    const [y, m, d] = decoded;
    if (y && y > 0 && m && m > 0 && d && d > 0) {
      return tryEncodeDate(y, m, d);
    }
  }
  return undefined;
};

export const convertExpected_tryDateStringToDate = (
  expected: [boolean, string?]
): Date | undefined => {
  const [ok, date] = expected;
  return ok && date ? dateFns.parseISO(date) : undefined;
};

/**
 * Converts date strings to dates.
 * @param {string} dateString - The date string to convert.
 * @param {Date} defaultValue - The default value to use if conversion fails.
 * @param {number?} [defaultMonth] - The default month value if 0 or not present.
 * @param {number?} [defaultDay] - The default day value if 0 or not present.
 * @returns {Date} The converted date value or the default value.
 */
export const dateStringToDateDef = (
  dateString: string,
  defaultValue: Date,
  defaultMonth?: number,
  defaultDay?: number
): Date => {
  const date = tryDateStringToDate(dateString, defaultMonth, defaultDay);
  return date ?? defaultValue;
};

/**
 * Converts date strings to dates.
 * @param {string} dateString - The date string to convert.
 * @param {Date?} defaultValue - The default value to use if conversion fails.
 * @param {number?} [defaultMonth] - The default month value if 0 or not present.
 * @param {number?} [defaultDay] - The default day value if 0 or not present.
 * @returns {Date | undefined} The converted date value or the default value.
 */
export const dateStringToDateDef2 = (
  dateString: string,
  defaultValue?: Date,
  defaultMonth?: number,
  defaultDay?: number
): Date | undefined => {
  const date = tryDateStringToDate(dateString, defaultMonth, defaultDay);
  return date ?? defaultValue;
};

/**
 * Checks if a date string is valid.
 * @param {string} dateString - The date string to validate.
 * @returns {boolean} True if the date string is valid, false otherwise.
 */
export const dateStringIsValid = (dateString: string): boolean => {
  const decoded = tryDecodeDateString(dateString);
  if (decoded) {
    return true;
  }
  return false;
};

/**
 * Checks if a date string represents a valid date.
 * @param {string} dateString - The date string to validate.
 * @returns {boolean} True if the date string represents a valid date, false otherwise.
 */
export const dateStringIsValidDate = (dateString: string): boolean => {
  const decoded = tryDecodeDateString(dateString);
  if (decoded) {
    const [y, m, d] = decoded;
    if (y && y > 0 && m && m > 0 && d && d > 0) {
      return true;
    }
  }
  return false;
};

/**
 * Compares two dates or date strings.
 * @param {Date | string} Value1 - The first date or date string.
 * @param {Date | string} Value2 - The second date or date string.
 * @returns {number} The comparison result: -1 if Value1 < Value2, 0 if Value1 === Value2, 1 if Value1 > Value2.
 */
export const compareDateString = (
  Value1: Date | string,
  Value2: Date | string
): number => {
  const date1 = dateToDateStringDef(Value1);
  const date2 = dateToDateStringDef(Value2);
  return stringCompare(date1, date2);
};

/**
 * Compares two dates or date strings. Only defined date parts are compared. I.e. 199902 is equal to 19990205.
 * @param {Date | string} Value1 - The first date or date string.
 * @param {Date | string} Value2 - The second date or date string.
 * @returns {number} The comparison result: -1 if Value1 < Value2, 0 if Value1 === Value2, 1 if Value1 > Value2.
 */
export const compareDateString2 = (
  Value1: Date | string,
  Value2: Date | string
): number => {
  const date1 = dateToDateStringDef(Value1);
  const date2 = dateToDateStringDef(Value2);
  const length = Math.min(date1.length, date2.length);
  return stringCompare(date1.substring(0, length), date2.substring(0, length));
};

/**
 * Creates a datetime string from given year, month, day, hour and minute.
 * @param {number | undefined} year - The year.
 * @param {number | undefined} month - The month.
 * @param {number | undefined} day - The day.
 * @param {number | undefined} hour - The hour.
 * @param {number | undefined} minute - The minute.
 * @returns {string | undefined} The datetime string if encoding is successful, undefined otherwise.
 */
export const tryEncodeDateTimeString = (
  year: number | undefined,
  month: number | undefined,
  day: number | undefined,
  hour: number | undefined,
  minute: number | undefined
): string | undefined => {
  const dateString = tryEncodeDateString(year, month, day);
  if (dateString !== undefined) {
    if (
      day !== undefined &&
      day > 0 &&
      hour !== undefined &&
      hour >= 0 &&
      hour <= 23 &&
      minute !== undefined &&
      minute >= 0 &&
      minute <= 59
    ) {
      return (
        dateString +
        hour.toString().padStart(2, '0') +
        minute.toString().padStart(2, '0')
      );
    }
    if (hour !== undefined || minute !== undefined) {
      return undefined;
    }
  }
  return dateString;
};

export const convertParams_tryEncodeDateTimeString = (
  params: [number, number, number, number, number]
): [
  number | undefined,
  number | undefined,
  number | undefined,
  number | undefined,
  number | undefined
] => {
  const [y, m, d, h, n] = params;
  return [
    y !== -1 ? y : undefined,
    m !== -1 ? m : undefined,
    d !== -1 ? d : undefined,
    h !== -1 ? h : undefined,
    n !== -1 ? n : undefined,
  ];
};

export const convertExpected_tryEncodeDateTimeString = (
  expected: [boolean, string?]
): string | undefined => {
  const [ok, dateString] = expected;
  return ok ? dateString : undefined;
};

/**
 * Creates a datetime string from given year, month, day, hour and minute.
 * @param {number | undefined} year - The year.
 * @param {number | undefined} month - The month.
 * @param {number | undefined} day - The day.
 * @param {number | undefined} hour - The hour.
 * @param {number | undefined} minute - The minute.
 * @param {string} defaultValue - The default value to use if encoding fails.
 * @returns {string} The encoded date string or the default value.
 */
export const encodeDateTimeStringDef = (
  year: number | undefined,
  month: number | undefined,
  day: number | undefined,
  hour: number | undefined,
  minute: number | undefined,
  defaultValue: string = ''
): string => {
  let value = tryEncodeDateTimeString(year, month, day, hour, minute);
  return value ?? defaultValue;
};

export const convertParams_encodeDateTimeStringDef = (
  params: [number, number, number, number, number, string]
): [
  number | undefined,
  number | undefined,
  number | undefined,
  number | undefined,
  number | undefined,
  string
] => {
  const [y, m, d, h, n, def] = params;
  return [
    y !== -1 ? y : undefined,
    m !== -1 ? m : undefined,
    d !== -1 ? d : undefined,
    h !== -1 ? h : undefined,
    n !== -1 ? n : undefined,
    def,
  ];
};

/**
 * Tries to decode a datetime string to its components (year, month, day, hour, minute). undefined means not present.
 * @param {string | Date} dateTimeString - The datetime string to decode.
 * @param {number | undefined} [defaultMonth] - The default month value if 0 or not present.
 * @param {number | undefined} [defaultDay] - The default day value if 0 or not present.
 * @returns {[number | undefined, number | undefined, number | undefined, number | undefined, number | undefined] | undefined} [year, month, day, hour, minute] if decoding is successful, undefined otherwise.
 */
export const tryDecodeDateTimeString = (
  dateTimeString: string | Date,
  defaultMonth?: number,
  defaultDay?: number
):
  | [
      number | undefined,
      number | undefined,
      number | undefined,
      number | undefined,
      number | undefined
    ]
  | undefined => {
  if (typeof dateTimeString === 'string') {
    const length = dateTimeString.length;
    const decoded = tryDecodeDateString(
      dateTimeString.substring(0, 8),
      length <= 8 ? defaultMonth : undefined,
      length <= 8 ? defaultDay : undefined
    );
    if (decoded) {
      const [y, m, d] = decoded;
      if (length === 12) {
        const h = strToInt(dateTimeString.substring(8, 10));
        const n = strToInt(dateTimeString.substring(10, 12));
        if (
          d &&
          d > 0 &&
          !isNaN(h) &&
          h >= 0 &&
          h <= 23 &&
          !isNaN(n) &&
          n >= 0 &&
          n <= 59
        ) {
          return [y, m, d, h, n];
        }
      } else if (length <= 8) {
        return [y, m, d, undefined, undefined];
      }
    }
    return undefined;
  } else {
    const [y, m, d] = getYearMonthDay(dateTimeString);
    const [h, n] = getHourMin(dateTimeString);
    return [y, m, d, h, n];
  }
};

export const convertParams_tryDecodeDateTimeString = (
  params: [string, number?, number?]
): [string, number | undefined, number | undefined] => {
  const [dateTimeString, defaultMonth, defaultDay] = params;
  return [
    dateTimeString,
    defaultMonth ?? -1 >= 0 ? defaultMonth : undefined,
    defaultDay ?? -1 >= 0 ? defaultDay : undefined,
  ];
};

export const convertExpected_tryDecodeDateTimeString = (
  expected: [boolean, number, number, number, number, number]
):
  | [
      number | undefined,
      number | undefined,
      number | undefined,
      number | undefined,
      number | undefined
    ]
  | undefined => {
  const [ok, y, m, d, h, n] = expected;
  return ok
    ? [
        y !== -1 ? y : undefined,
        m !== -1 ? m : undefined,
        d !== -1 ? d : undefined,
        h !== -1 ? h : undefined,
        n !== -1 ? n : undefined,
      ]
    : undefined;
};

/**
 * Converts datetimes to datetime strings.
 * @param {Date | string} dateTime - The datetime value to convert.
 * @returns {string | undefined} The date string if conversion is successful, undefined otherwise.
 */
export const tryDateTimeToDateTimeString = (
  dateTime: Date | string
): string | undefined => {
  const decoded = tryDecodeDateTimeString(dateTime);
  if (decoded) {
    const [y, m, d, h, n] = decoded;
    return tryEncodeDateTimeString(y, m, d, h, n);
  } else {
    return undefined;
  }
};

export const convertExpected_tryDateTimeToDateTimeString = (
  expected: [boolean, string?]
): string | undefined => {
  const [ok, dateTimeString] = expected;
  return ok ? dateTimeString : undefined;
};

/**
 * Converts datetimes to datetime strings.
 * @param {Date | string} dateTime - The datetime value to convert.
 * @param {string} defaultValue - The default value to use if conversion fails.
 * @returns {string} The converted date string or the default value.
 */
export const dateTimeToDateTimeStringDef = (
  dateTime: Date | string,
  defaultValue: string = ''
): string => {
  const value = tryDateTimeToDateTimeString(dateTime);
  return value ?? defaultValue;
};

/**
 * Tries to convert a date and a time value to a datetime string.
 * @param {Date} date - The date value.
 * @param {Date} time - The time value.
 * @returns {string | undefined} The datetime string if conversion is successful, undefined otherwise.
 */
export const tryDateAndTimeToDateTimeString = (
  date: Date,
  time: Date
): string | undefined => {
  const [y, m, d] = getYearMonthDay(date);
  const [h, n] = getHourMin(time);
  return tryEncodeDateTimeString(y, m, d, h, n);
};

export const convertExpected_tryDateAndTimeToDateTimeString = (
  expected: [boolean, string?]
): string | undefined => {
  const [ok, date] = expected;
  return ok && date ? date : undefined;
};

/**
 * Tries to convert a date and a time value to a datetime string.
 * @param {Date} date - The date value.
 * @param {Date} time - The time value.
 * @param {string} defaultValue - The default value to use if conversion fails.
 * @returns {string} The datetime string if conversion is successful, the default value otherwise.
 */
export const dateAndTimeToDateTimeStringDef = (
  date: Date,
  time: Date,
  defaultValue: string = ''
): string => {
  const dateTime = tryDateAndTimeToDateTimeString(date, time);
  return dateTime ?? defaultValue;
};

/**
 * Tries to convert a datetime string to a date value.
 * @param {string} dateTimeString - The date and time string to convert.
 * @param {number} [defaultMonth=1] - The default month value if conversion fails.
 * @param {number} [defaultDay=1] - The default day value if conversion fails.
 * @returns {Date | undefined} The datetime if conversion is successful, undefined otherwise.
 */
export const tryDateTimeStringToDateTime = (
  dateTimeString: string,
  defaultMonth: number = 1,
  defaultDay: number = 1
): Date | undefined => {
  const decoded = tryDecodeDateTimeString(
    dateTimeString,
    defaultMonth,
    defaultDay
  );
  if (decoded) {
    const [y, m, d, h, n] = decoded;
    if (y && m && d && d > 0) return tryEncodeDateTime(y, m, d, h ?? 0, n ?? 0);
  }
  return undefined;
};

export const convertExpected_tryDateTimeStringToDateTime = (
  expected: [boolean, string?]
): Date | undefined => {
  const [ok, date] = expected;
  return ok && date ? dateFns.parseISO(date) : undefined;
};

/**
 * Converts a date and time string to a date value with a default value if conversion fails.
 * @param {string} dateTimeString - The date and time string to convert.
 * @param {Date} defaultValue - The default value to use if conversion fails.
 * @param {number} [defaultMonth=1] - The default month value if conversion fails.
 * @param {number} [defaultDay=1] - The default day value if conversion fails.
 * @returns {Date} The datetime if conversion is successful, the default value otherwise.
 */
export const dateTimeStringToDateTimeDef = (
  dateTimeString: string,
  defaultValue: Date,
  defaultMonth: number = 1,
  defaultDay: number = 1
): Date => {
  const dateTime = tryDateTimeStringToDateTime(
    dateTimeString,
    defaultMonth,
    defaultDay
  );
  return dateTime ?? defaultValue;
};

/**
 * Converts a date and time string to a date value with a default value if conversion fails.
 * @param {string} dateTimeString - The date and time string to convert.
 * @param {Date | undefined} defaultValue - The default value to use if conversion fails.
 * @param {number} [defaultMonth=1] - The default month value if conversion fails.
 * @param {number} [defaultDay=1] - The default day value if conversion fails.
 * @returns {Date | undefined} The datetime if conversion is successful, the default value otherwise.
 */
export const dateTimeStringToDateTimeDef2 = (
  dateTimeString: string,
  defaultValue: Date | undefined,
  defaultMonth: number = 1,
  defaultDay: number = 1
): Date | undefined => {
  const dateTime = tryDateTimeStringToDateTime(
    dateTimeString,
    defaultMonth,
    defaultDay
  );
  return dateTime ?? defaultValue;
};

/**
 * Checks if a datetime string is valid.
 * @param {string} dateTimeString - The datetime string to validate.
 * @returns {boolean} True if the datetime string is valid, false otherwise.
 */
export const dateTimeStringIsValid = (dateTimeString: string): boolean => {
  const decoded = tryDecodeDateTimeString(dateTimeString);
  if (decoded) {
    return true;
  }
  return false;
};

/**
 * Checks if a datetime string represents a valid date.
 * @param {string} dateTimeString - The datetime string to validate.
 * @returns {boolean} True if the datetime string represents a valid date, false otherwise.
 */
export const dateTimeStringIsValidDate = (dateTimeString: string): boolean => {
  const decoded = tryDecodeDateTimeString(dateTimeString);
  if (decoded) {
    const [y, m, d] = decoded;
    if (y && y > 0 && m && m > 0 && d && d > 0) {
      return true;
    }
  }
  return false;
};

/**
 * Compares two datetimes or datetime strings.
 * @param {Date | string} Value1 - The first datetime or datetime string.
 * @param {Date | string} Value2 - The second datetime or datetime string.
 * @returns {number} The comparison result: -1 if Value1 < Value2, 0 if Value1 === Value2, 1 if Value1 > Value2.
 */
export const compareDateTimeString = (
  Value1: Date | string,
  Value2: Date | string
): number => {
  const date1 = dateTimeToDateTimeStringDef(Value1);
  const date2 = dateTimeToDateTimeStringDef(Value2);
  return stringCompare(date1, date2);
};

/**
 * Compares two datetimes or datetime strings. Only defined datetime parts are compared. I.e. 199902 is equal to 19990205.
 * @param {Date | string} Value1 - The first datetime or datetime string.
 * @param {Date | string} Value2 - The second datetime or datetime string.
 * @returns {number} The comparison result: -1 if Value1 < Value2, 0 if Value1 === Value2, 1 if Value1 > Value2.
 */
export const compareDateTimeString2 = (
  Value1: Date | string,
  Value2: Date | string
): number => {
  const date1 = dateTimeToDateTimeStringDef(Value1);
  const date2 = dateTimeToDateTimeStringDef(Value2);
  const length = Math.min(date1.length, date2.length);
  return stringCompare(date1.substring(0, length), date2.substring(0, length));
};

export const minDate = (date1: Date, date2: Date): Date =>
  diffMSec(date1, date2) >= 0 ? date1 : date2;

export const maxDate = (date1: Date, date2: Date): Date =>
  diffMSec(date1, date2) <= 0 ? date1 : date2;

export const sameDate = (date1: Date, date2: Date): boolean =>
  diffMSec(date1, date2) === 0;

export const dateNavigatorFormatter = (startDay: string, endDay: string) => {
  const MONTHNAMES = [
    'januari',
    'februari',
    'mars',
    'april',
    'maj',
    'juni',
    'juli',
    'augusti',
    'september',
    'oktober',
    'november',
    'december',
  ];
  const MONTHNAMES_SHORT = [
    'jan',
    'feb',
    'mar',
    'apr',
    'maj',
    'jun',
    'jul',
    'aug',
    'sep',
    'okt',
    'nov',
    'dec',
  ];
  const startDayDate = startDay.split('-');
  const endDayDate = endDay.split('-');
  const startMonth = parseInt(startDayDate[1]);
  const endMonth = parseInt(endDayDate[1]);
  const startYear = parseInt(startDayDate[0]);
  const endYear = parseInt(endDayDate[0]);
  const startMonthName = MONTHNAMES[startMonth - 1];
  const startMonthNameShort = MONTHNAMES_SHORT[startMonth - 1];
  const endMonthNameShort = MONTHNAMES_SHORT[endMonth - 1];
  const startDayNumber = parseInt(startDayDate[2]);
  const endDayNumber = parseInt(endDayDate[2]);

  if (startYear === endYear) {
    if (startMonth === endMonth) {
      if (startDayNumber === endDayNumber) {
        return `${startMonthName} ${startYear}`;
      } else {
        return `${startMonthName} ${startYear}`;
      }
    } else {
      return `${startMonthNameShort} - ${endMonthNameShort} ${startYear}`;
    }
  } else {
    return `${startMonthNameShort} ${startYear} -${endMonthNameShort} ${endYear}`;
  }
};

export const getLabelForSpanningWeek = (
  currentDate: Date,
  showYear: boolean = true,
  useFullMonthNames: boolean = false
): string | undefined => {
  if (!(currentDate instanceof Date) || isNaN(currentDate.getTime())) {
    return undefined;
  }

  const startOfWeek = firstDayInWeek(currentDate);
  const endOfWeek = lastDayInWeek(currentDate);

  const startMonth = startOfWeek.getMonth();
  const endMonth = endOfWeek.getMonth();
  const startYear = startOfWeek.getFullYear();
  const endYear = endOfWeek.getFullYear();

  if (startMonth !== endMonth || startYear !== endYear) {
    const startMonthText = getMonthText(startMonth + 1, !useFullMonthNames);
    const endMonthText = getMonthText(endMonth + 1, !useFullMonthNames);

    if (startYear !== endYear) {
      return showYear
        ? `${startMonthText} ${startYear} - ${endMonthText} ${endYear}`
        : `${startMonthText}-${endMonthText}`;
    } else {
      return showYear
        ? `${startMonthText} - ${endMonthText} ${startYear}`
        : `${startMonthText}-${endMonthText}`;
    }
  }
  return undefined;
};
